import { useState, useEffect } from "react";

import api from "../api/api.js";
import endpoint from "../api/endpoints.js";

import { SHIPPING_DEFAULT, SFC_FREE_SHIPPING } from "@/constants/constant.js";

/* =======================
   Order/Number utils (NEW)
   ======================= */

// first non-null/undefined/empty value
export const pick = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "");

// robust numeric parse (handles strings like "$1,234.50")
export function toNum(v) {
  if (v === undefined || v === null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export const round2 = (n) =>
  Math.round((toNum(n) + Number.EPSILON) * 100) / 100;

/**
 * Normalize a SuiteQL/REST order line into a predictable, positive-display shape.
 * Ensures qty, rate, amount are ABS() so no leading "-" in the UI.
 */
export function normalizeOrderLine(row) {
  const rawQty = toNum(
    pick(row.total_quantity, row.quantity, row.qty, row.custcol_quantity, 0)
  );
  const quantity = Math.abs(rawQty);

  const rawRate = toNum(
    pick(row.rate, row.unitprice, row.price, row.rateamount, 0)
  );
  const rate = Math.abs(rawRate);

  const rawAmount = toNum(
    pick(row.netamount, row.rateamount, row.amount, quantity * rate)
  );
  const amount = Math.abs(rawAmount);

  return {
    lineId:
      pick(row.line, row.linenumber, row.lineId, row.seq) ?? Math.random(),
    productId: pick(row.item, row.itemId, row.item_id, row.internalid, row.id), // <-- add this
    sku: pick(row.itemid, row.itemId, row.item_id, row.sku, ""),
    name: pick(
      row.itemname,
      row.item_displayname,
      row.displayname,
      row.itemid,
      row.name,
      "Item"
    ),
    quantity,
    rate,
    amount,
    image: pick(row.file_url, row.image, row.thumbnail, row.fileurl, ""),
  };
}

/** Sum line amounts (robust: prefers amount, falls back to qty*rate; always positive) */
export function computeLinesSubtotal(lines = []) {
  const sum = lines.reduce((acc, l) => {
    const qty = Math.abs(toNum(l?.quantity));
    const rate = Math.abs(toNum(l?.rate));
    const amount = toNum(l?.amount);
    const lineTotal = Math.abs(amount > 0 ? amount : round2(qty * rate));
    return acc + lineTotal;
  }, 0);
  return round2(sum);
}

/**
 * Prefer header total (may include tax/shipping/fees) with sensible fallbacks.
 * Tries: totalAfterDiscount → foreigntotal → total → grandtotal; uses ABS() and
 * falls back to the (positive) subtotal when header isn't a valid positive number.
 */
export function computeOrderTotalFromSummary(
  summary = {},
  fallbackSubtotal = 0
) {
  const headerRaw = pick(
    summary?.totalAfterDiscount,
    summary?.foreigntotal,
    summary?.total,
    summary?.grandtotal
  );
  const header = Math.abs(toNum(headerRaw));
  const subtotal = Math.abs(toNum(fallbackSubtotal));
  const total = header > 0 ? header : subtotal;
  return round2(total);
}

/* =======================
   Existing utilities
   ======================= */

function extractBuyGet(str) {
  const numbers = str.match(/\d+/g);
  return {
    buy: numbers ? parseInt(numbers[0], 10) : null,
    get: numbers ? parseInt(numbers[1], 10) : null,
  };
}

// Parse custitem38 to get matrix type and options
function getMatrixInfo(matrixOptions) {
  if (matrixOptions.length === 0) return { matrixType: "", options: [] };

  try {
    // Get the first item to determine the matrix type
    const firstItem = matrixOptions[0];
    if (firstItem.custitem38) {
      // Parse as JavaScript object string (not JSON)
      const objectString = firstItem.custitem38.replace(/'/g, '"'); // Replace single quotes with double quotes
      const parsed = JSON.parse(objectString);
      const matrixType = Object.keys(parsed)[0]; // e.g., "Shade"

      const options = matrixOptions.map((item) => {
        try {
          const itemObjectString = item.custitem38.replace(/'/g, '"');
          const itemParsed = JSON.parse(itemObjectString);
          const value = itemParsed[matrixType]; // e.g., "Shade A2"
          return {
            value: item.id,
            label: value || item.itemid,
          };
        } catch {
          return {
            value: item.id,
            label: item.itemid,
          };
        }
      });

      return { matrixType, options };
    }
  } catch {
    // Fallback if parsing fails
  }

  return {
    matrixType: "Options",
    options: matrixOptions.map((item) => ({
      value: item.id,
      label: item.custitem38 || item.itemid,
    })),
  };
}

// Format price with proper rounding to 2 decimal places and comma separators
function formatPrice(price) {
  // Handle null, undefined, or invalid values
  if (price === null || price === undefined || isNaN(price)) {
    return "0.00";
  }

  // Convert to number, format to 2 decimal places, and add comma separators
  return Number(price).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Truncate text to specified length and add ellipsis
function truncateText(text, maxLength = 30) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

// Parse payment status from memo text and return formatted status
function parsePaymentStatus(memo) {
  if (!memo) return null;

  const upperMemo = memo.toUpperCase();

  // Define payment status patterns with their display info
  const statusPatterns = [
    {
      keywords: ["SUCCESSFUL", "SUCCESS", "COMPLETED", "PAID"],
      status: "SUCCESSFUL",
      color: "text-green-600",
      bgColor: "bg-green-50",
      icon: "✓",
    },
    {
      keywords: ["PENDING", "PROCESSING", "IN PROGRESS"],
      status: "PENDING",
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      icon: "⏳",
    },
    {
      keywords: ["FAILED", "FAILURE", "ERROR", "DECLINED"],
      status: "FAILED",
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: "✗",
    },
    {
      keywords: ["CANCELED", "CANCELLED", "REFUNDED"],
      status: "CANCELED",
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      icon: "⊘",
    },
    {
      keywords: ["UNKNOWN", "UNCLEAR", "UNDEFINED"],
      status: "UNKNOWN",
      color: "text-gray-500",
      bgColor: "bg-gray-50",
      icon: "?",
    },
  ];

  // Find matching status
  for (const pattern of statusPatterns) {
    if (pattern.keywords.some((keyword) => upperMemo.includes(keyword))) {
      return {
        status: pattern.status,
        color: pattern.color,
        bgColor: pattern.bgColor,
        icon: pattern.icon,
        displayText: pattern.status,
      };
    }
  }

  // If no pattern matches, return null to show original text
  return null;
}

// Derive payment method (on account vs. credit card) from an order's memo.
// NetSuite's own `terms`/`paymentmethod` transaction fields are never set by
// checkout (the invoice path leaves them untouched, and NetSuite's standard
// `paymentmethod` field turned out to be read-only via REST for Sales Order),
// so memo text is the only reliable signal. The two order-creation paths
// write structurally different memos: the invoice path always sets
// `"Online – <tranid>"` (`order.service.js`), while the card path's memo
// always contains "STRIPE Payment" (`paymentMemo()` in
// `stripe.payment.service.js`) regardless of PENDING/SUCCESSFUL/FAILED state.
function getPaymentMethodFromMemo(memo) {
  if (!memo) return null;
  return memo.toUpperCase().includes("STRIPE PAYMENT")
    ? "Credit Card"
    : "On Account";
}

// Format price with currency symbol
function formatCurrency(price, currency = "$") {
  return `${currency}${formatPrice(price)}`;
}

// Calculate total price for quantity
function calculateTotalPrice(unitPrice, quantity) {
  if (!unitPrice || !quantity) return "0.00";

  const total = Number(unitPrice) * Number(quantity);
  return formatPrice(total);
}

// Calculate total price with currency symbol
function calculateTotalCurrency(unitPrice, quantity, currency = "$") {
  return `${currency}${calculateTotalPrice(unitPrice, quantity)}`;
}

// The business's real free-shipping threshold. Checkout math and the cart drawer's
// progress bar must agree on this number — the design reference's fixture says
// $250, but the order calculation below has always used $300, so $300 is the
// truth the UI shows. The marketing copy (Header strip, HomeHero chip,
// ProductDetail perk) was aligned to $300 on 2026-10-19; it is still three
// literal strings, so a change here means changing those too.
export const FREE_SHIPPING_THRESHOLD = 300;

// Calculate order total with shipping and tax
// Tax the way NetSuite computes it: per line, rounded per line, shipping as
// its own taxed line. A single `(subtotal + shipping) × rate` can land one cent
// below NetSuite's total (verified on SO119157: 7.80 + 6.11 + 1.30 = 15.21 vs
// 116.96 × 13% = 15.20), and the Stripe hold can never be captured above what
// was authorized — so the estimate must round exactly as NetSuite does.
// `lines` are `{ unitPrice, quantity }` (or `{ amount }`); `rate` is a fraction
// (0.13), the same number GET_TAX_RATES returns as `rates.total`.
//
// All arithmetic is in integer cents with an explicit half-up, never on
// floats: 80.30 × 0.05 is 4.015 on paper but 4.0149999999999997 in binary, so
// `round2(amount * rate)` gave 4.01 where NetSuite books 4.02 — one cent short
// and the order was closed as "total changed" (Oct 2026 review; 5%, 11%, 15%
// and 14.975% provinces all hit it, 13% only by luck). The rate is scaled to
// micro-units so Quebec's 14.975% stays exact.
function taxCentsHalfUp(amountCents, rateMicro) {
  return Math.floor((amountCents * rateMicro) / 1e6 + 0.5);
}

function estimateTaxPerLine(lines = [], shippingCost = 0, rate = 0) {
  const rateMicro = Math.round(toNum(rate) * 1e6);
  if (!rateMicro) return 0;
  const lineTaxCents = lines.reduce((sum, line) => {
    const amountCents =
      line?.amount !== undefined
        ? Math.round(toNum(line.amount) * 100)
        : Math.round(
            round2(toNum(line?.unitPrice) * toNum(line?.quantity)) * 100
          );
    return sum + taxCentsHalfUp(amountCents, rateMicro);
  }, 0);
  const shippingCents = Math.round(toNum(shippingCost) * 100);
  return (lineTaxCents + taxCentsHalfUp(shippingCents, rateMicro)) / 100;
}

function calculateOrderTotal(
  subtotal,
  shippingCost = null,
  estimatedTax = null
) {
  const shipping =
    subtotal >= FREE_SHIPPING_THRESHOLD
      ? 0
      : shippingCost !== null
        ? shippingCost
        : 9.99;
  const tax = estimatedTax || 0;
  const total = Number(subtotal) + Number(tax) + Number(shipping);

  return {
    shipping,
    tax,
    total,
  };
}

// Calculate total price after discount based on promotions
// async function getTotalPriceAfterDiscount(productId, unitPrice, quantity) {
//   try {
//     // Import api and endpoints here to avoid circular dependency
//     const { default: api } = await import("../api/api.js");
//     const { default: endpoint } = await import("../api/endpoints.js");

//     // Fetch promotions for the product
//     const promotionUrl = endpoint.GET_PROMOTIONS_BY_PRODUCT({
//       productId,
//     });

//     const response = await api.get(promotionUrl);
//     const promotions = response.data;

//     if (!promotions || promotions.length === 0) {
//       return {
//         originalPrice: Number(unitPrice) * Number(quantity),
//         discountedPrice: Number(unitPrice) * Number(quantity),
//         discount: 0,
//         promotionApplied: null,
//       };
//     }

//     const originalTotal = Number(unitPrice) * Number(quantity);
//     let bestDiscount = 0;
//     let bestPromotion = null;

//     // Check each promotion to find the best discount
//     for (const promotion of promotions) {
//       const { fixedprice, itemquantifier } = promotion;

//       // Skip if promotion doesn't have required fields
//       if (!fixedprice || !itemquantifier) continue;

//       // Check if quantity meets the minimum requirement
//       if (Number(quantity) >= Number(itemquantifier)) {
//         // Calculate how many promotion sets can be applied
//         const promotionSets = Math.floor(
//           Number(quantity) / Number(itemquantifier)
//         );
//         const remainingItems = Number(quantity) % Number(itemquantifier);

//         // Calculate discounted price
//         const discountedTotal =
//           promotionSets * Number(fixedprice) +
//           remainingItems * Number(unitPrice);
//         const discount = originalTotal - discountedTotal;

//         // Keep track of best discount
//         if (discount > bestDiscount) {
//           bestDiscount = discount;
//           bestPromotion = promotion;
//         }
//       }
//     }

//     return {
//       originalPrice: originalTotal,
//       discountedPrice: originalTotal - bestDiscount,
//       discount: bestDiscount,
//       promotionApplied: bestPromotion,
//     };
//   } catch (error) {
//     // Return original price if error occurs
//     const originalTotal = Number(unitPrice) * Number(quantity);
//     return {
//       originalPrice: originalTotal,
//       discountedPrice: originalTotal,
//       discount: 0,
//       promotionApplied: null,
//       error: error.message,
//     };
//   }
// }

// Postal code lookup function (simplified - now uses separate validation)
async function fetchLocationByPostalCode(country, code) {
  if (!code || !code.trim()) {
    return {
      success: false,
      error: "Postal code is required",
    };
  }

  // Use the new validation function
  const validationError = validatePostalCode(code, country);
  if (validationError) {
    return {
      success: false,
      error: validationError,
    };
  }

  try {
    const cleanCode = code.replace(/\s/g, "").toUpperCase();
    let apiUrl = "";

    if (country === "us") {
      apiUrl = endpoint.GET_POSTAL_LOOKUP("us", cleanCode);
    } else if (country === "ca") {
      const firstThree = cleanCode.slice(0, 3);
      apiUrl = endpoint.GET_POSTAL_LOOKUP("ca", firstThree);
    } else {
      return {
        success: false,
        error: 'Country not supported. Only "us" and "ca" are supported.',
      };
    }

    const response = await fetch(apiUrl);

    if (!response.ok) {
      return {
        success: false,
        error: `Postal code not found (${response.status})`,
      };
    }

    const data = await response.json();

    if (data.places && data.places.length > 0) {
      return {
        success: true,
        data: {
          city: data.places[0]["place name"],
          province: data.places[0]["state abbreviation"],
          state: data.places[0]["state abbreviation"], // Alias for province
          country: data.country,
          latitude: data.places[0]["latitude"],
          longitude: data.places[0]["longitude"],
        },
      };
    } else {
      return {
        success: false,
        error: "No location data found",
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to fetch location data",
    };
  }
}

// Quantity handling utilities for Buy X Get Y promotions
function calculateActualQuantity(selectedQuantity, stockdescription) {
  const { buy, get } = extractBuyGet(stockdescription || "");

  if (!buy || !get) {
    return selectedQuantity;
  }

  // Calculate how many complete "buy" sets the user has selected
  const buyMultiplier = Math.floor(selectedQuantity / buy);
  const remainder = selectedQuantity % buy;

  // Calculate total quantity: (buy + get) * multiplier + remainder
  return buyMultiplier * (buy + get) + remainder;
}

// Create quantity handler functions
function createQuantityHandlers(
  quantity,
  setQuantity,
  setActualQuantity,
  stockdescription
) {
  const updateQuantities = (newQuantity) => {
    setQuantity(newQuantity);
    setActualQuantity(calculateActualQuantity(newQuantity, stockdescription));
  };

  return {
    handleQuantityChange: (e) => {
      const value = e.target.value;
      if (Number(value) < 1) {
        updateQuantities(1);
      } else {
        updateQuantities(Number(value));
      }
    },

    increment: () => {
      const newQuantity = Number(quantity) + 1;
      updateQuantities(newQuantity);
    },

    decrement: () => {
      if (Number(quantity) > 1) {
        const newQuantity = Number(quantity) - 1;
        updateQuantities(newQuantity);
      }
    },
  };
}

// Hook-like utility for quantity management with Buy X Get Y logic
function useQuantityHandlers(initialQuantity = 1, stockdescription = "") {
  const [quantity, setQuantity] = useState(initialQuantity);
  const [actualQuantity, setActualQuantity] = useState(
    calculateActualQuantity(initialQuantity, stockdescription)
  );

  // Update actual quantity when stockdescription changes
  useEffect(() => {
    setActualQuantity(calculateActualQuantity(quantity, stockdescription));
  }, [quantity, stockdescription]);

  // Handler for typed input from QuantityControls
  // Signature matches QuantityControls onChange: (itemId, newQty)
  const handleQuantityChange = (itemId, newQty) => {
    const validQty = Math.max(1, parseInt(newQty, 10) || 1);
    setQuantity(validQty);
    // actualQuantity will be updated by useEffect
  };

  // Direct increment/decrement handlers (no itemId needed)
  const increment = () => {
    setQuantity((prev) => Number(prev) + 1);
    // actualQuantity will be updated by useEffect
  };

  const decrement = () => {
    setQuantity((prev) => (Number(prev) > 1 ? Number(prev) - 1 : 1));
    // actualQuantity will be updated by useEffect
  };

  return {
    quantity,
    actualQuantity,
    handleQuantityChange,
    increment,
    decrement,
    setQuantity: (newQuantity) => {
      setQuantity(newQuantity);
      setActualQuantity(calculateActualQuantity(newQuantity, stockdescription));
    },
    setActualQuantity,
  };
}

async function fetchRegionByCode(country, code) {
  try {
    // Only allow 'ca' or 'us'
    if (country !== "ca" && country !== "us") {
      throw new Error('Country not supported. Only "ca" and "us" are allowed.');
    }
    let cleanCode = code.replace(/\s/g, "").toUpperCase();
    if (country === "ca") {
      cleanCode = cleanCode.slice(0, 3); // Use only first 3 characters for Canada
    }
    const response = await api.get(
      endpoint.GET_POSTAL_LOOKUP(country, cleanCode)
    );
    return response.data;
  } catch (error) {
    return null;
  }
}

/**
 * The number as 10 bare digits — formatting and a leading country code 1
 * dropped — or null when it doesn't have 10. A "+1" prefix is stripped before
 * counting: our stored "+1" + 9 typed digits is 10 digits, and must not pass.
 */
function phoneTenDigits(text) {
  const digits = String(text ?? "")
    .replace(/^\s*\+1/, "")
    .replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
  return ten.length === 10 ? ten : null;
}

/**
 * Optional-phone check: "" is valid; anything else needs 10 digits, the first
 * one 2–9 (no North American area code starts with 0 or 1).
 */
function validatePhone(phone) {
  if (!phone) return true;
  const ten = phoneTenDigits(phone);
  return ten !== null && /^[2-9]/.test(ten);
}

// Password validation function
function validatePassword(password) {
  // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(
    password
  );
}

// Postal code validation functions
function validateCanadianPostalCode(code) {
  // Canadian postal code format: A1A 1A1 (letter-digit-letter space digit-letter-digit)
  const canadianPattern = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
  return canadianPattern.test(code);
}

function validateUSZipCode(code) {
  // US zip code format: 12345 or 12345-6789
  const usPattern = /^\d{5}(-\d{4})?$/;
  return usPattern.test(code);
}

function validatePostalCode(code, selectedCountry) {
  if (!code.trim()) {
    return "Postal/Zip code is required";
  }

  if (selectedCountry === "ca") {
    return validateCanadianPostalCode(code)
      ? ""
      : "Please enter a valid Canadian postal code (e.g., A1A 1A1)";
  } else if (selectedCountry === "us") {
    return validateUSZipCode(code)
      ? ""
      : "Please enter a valid US zip code (e.g., 12345 or 12345-6789)";
  }

  return "";
}

// Reusable estimate tax and shipping function
async function handleTaxShippingEstimate({
  country,
  postalCode,
  state = "",
  subtotal,
  lines = null,
  userCategory,
  onSuccess,
  onError,
  onLoading,
  onDismiss,
}) {
  // Import modules dynamically to avoid circular dependencies
  const { default: endpoint } = await import("../api/endpoints.js");

  try {
    // Normalize country values to lowercase codes
    let normalizedCountry = country?.toLowerCase()?.trim();

    // Map all possible country variations to standardized codes
    const countryMap = {
      ca: "ca",
      canada: "ca",
      can: "ca",
      us: "us",
      usa: "us",
      america: "us",
    };

    normalizedCountry = countryMap[normalizedCountry];

    // Ensure we only accept valid normalized countries
    if (normalizedCountry !== "ca" && normalizedCountry !== "us") {
      const errorMessage =
        "Country not supported. Only Canada (ca) and United States (us) are supported.";
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    }

    // Validate postal code before making API call
    const validationError = validatePostalCode(postalCode, normalizedCountry);
    if (validationError) {
      if (onError) onError(validationError);
      return { success: false, error: validationError };
    }

    if (onLoading) onLoading("Estimating region...");

    let province = state; // Use provided state as fallback

    try {
      const result = await fetchRegionByCode(normalizedCountry, postalCode);

      if (result && result.places && result.places[0]) {
        // Use province from postal code lookup if available
        province =
          result.places[0]?.state ||
          result.places[0]?.state_abbreviation ||
          state;
      }
    } catch (regionError) {
      // If fetchRegionByCode fails, continue with the provided state parameter
      // Fallback to state parameter
    }

    if (onDismiss) onDismiss();

    // If we have a province (either from lookup or from state param), get tax rates
    if (province) {
      try {
        // Get tax rates
        const taxUrl = endpoint.GET_TAX_RATES({
          country: normalizedCountry,
          province,
        });
        const taxRates = await api.get(taxUrl);

        // Get shipping cost first using conditional shipping method
        const categorySFC = userCategory === 5 || userCategory === "5";
        const shippingMethodId = categorySFC
          ? SFC_FREE_SHIPPING
          : SHIPPING_DEFAULT;

        const shippingRes = await api.get(
          endpoint.GET_SHIPPING_METHOD(Number(shippingMethodId))
        );
        const shippingCost =
          shippingRes.data[0]?.shippingflatrateamount ?? 9.99;

        // Calculate final shipping cost (free shipping over the threshold)
        const finalShippingCost =
          Number(subtotal) >= FREE_SHIPPING_THRESHOLD ? 0 : shippingCost;

        // Estimated tax, rounded per line like NetSuite (see estimateTaxPerLine)
        const totalTaxRate = taxRates.data?.rates?.total;
        let estimatedTax = null;

        if (totalTaxRate && subtotal) {
          estimatedTax =
            Array.isArray(lines) && lines.length
              ? estimateTaxPerLine(lines, finalShippingCost, totalTaxRate)
              : // No lines known: single rounding on subtotal + shipping. Can sit
                // one cent under NetSuite, so checkout must always pass `lines`.
                round2(
                  (Number(subtotal) + Number(finalShippingCost)) *
                    Number(totalTaxRate)
                );
        }

        // `finalShippingCost`, not the raw flat rate: it's the number the tax
        // above was computed with, and the summary says "Free" only when it
        // sees 0. Returning the flat rate made the Delivery line read "$9.99"
        // on a free-shipping order while the Total (which applies the
        // threshold itself in calculateOrderTotal) correctly left it out.
        const estimateData = {
          estimatedTax,
          shippingCost: finalShippingCost,
          taxRate: totalTaxRate,
          province,
          country: normalizedCountry,
        };

        // Data only — no success message. The result is visible in the
        // summary itself (tax line, delivery line, total), so a "Tax rates
        // loaded!" toast on top of it was noise (removed 2026-10-19). The
        // loading toast above still shows while the lookups run.
        if (onSuccess) onSuccess(estimateData);

        return {
          success: true,
          data: estimateData,
        };
      } catch (taxError) {
        const errorMessage = "Failed to get tax rates or shipping cost.";
        if (onError) onError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } else {
      const errorMessage = "Province/State is required for tax calculation.";
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    }
  } catch (err) {
    if (onDismiss) onDismiss();
    const errorMessage = err.message || "Region lookup failed.";
    if (onError) onError(errorMessage);
    return { success: false, error: errorMessage };
  }
}

// The duplicate-order window shared by both order paths. The card path's
// backend key (stripe.payment.service.js IDEMPOTENCY_WINDOW_MINUTES) is 10
// minutes; on account used 5 until Oct 2026, so the same cart got "already
// placed" on one path and a second order on the other.
export const ORDER_IDEMPOTENCY_WINDOW_MINUTES = 10;

// The one "already placed" message for both order paths, so the number it
// quotes can never drift from the window again (PaymentForm said 5 after the
// window became 10 — Oct 2026 review).
function duplicateOrderMessage() {
  return `This order has already been placed. If you need to order again, please wait ${ORDER_IDEMPOTENCY_WINDOW_MINUTES} minutes and try again.`;
}

// cyrb53 (public domain): a fast, well-mixed 53-bit string hash. Not for
// security — only to turn an arbitrary-length string into a fixed-length key.
function hash53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

// Build idempotency key for order deduplication
function buildIdempotencyKey(
  userId,
  cartItems,
  userCategory = null,
  shipMethodId = null,
  windowMins = ORDER_IDEMPOTENCY_WINDOW_MINUTES
) {
  // Use conditional shipping method if not explicitly provided
  const actualShipMethodId =
    shipMethodId ||
    (userCategory === 5 || userCategory === "5"
      ? SFC_FREE_SHIPPING
      : SHIPPING_DEFAULT);

  const cartKey = (cartItems || [])
    .map((i) => `${i.netsuiteId || i.id}x${i.quantity}`)
    .sort()
    .join("|");

  // Include time window - allows duplicate orders after windowMins
  const windowBucket = Math.floor(Date.now() / (windowMins * 60 * 1000));
  const raw = `${userId || "anon"}|${actualShipMethodId}|${cartKey}|${windowBucket}`;

  // Hash, never truncate: the old `base64(raw).slice(0, 64)` dropped the
  // window bucket on carts of ~5+ lines, so the key never expired and the
  // same cart was "already placed" forever (Oct 2026 review). Two seeded
  // 53-bit hashes (~106 bits), base36 — compact and header-safe.
  return `ck-${hash53(raw, 1).toString(36)}${hash53(raw, 2).toString(36)}`;
}

// Generate a secure random password for NetSuite user creation
function generateRandomPassword(length = 16) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

// Calculate next run date for custrecord_ro_next_run field
function calculateNextRunDate(interval, intervalUnit) {
  const currentDate = new Date();
  const parsedInterval = parseInt(interval) || 1;
  const normalizedUnit = (intervalUnit || "week").toLowerCase();

  const nextRunDate = new Date(currentDate);

  switch (normalizedUnit) {
    case "week":
      nextRunDate.setDate(currentDate.getDate() + parsedInterval * 7);
      break;
    case "month":
      nextRunDate.setMonth(currentDate.getMonth() + parsedInterval);
      break;

    default:
      nextRunDate.setMonth(currentDate.getMonth() + parsedInterval);
  }

  return nextRunDate.toISOString().slice(0, 10); // yyyy-mm-dd format
}

// Calculate quantity allocation for order items
function calculateQuantityAllocation(item) {
  const quantity = Number(item.quantity);
  const totalquantityonhand = Number(item.totalquantityonhand) || 0;

  let quantityBackOrdered = 0;
  let quantityCommitted = 0;

  if (quantity > totalquantityonhand) {
    // Backordered scenario: quantity exceeds available stock
    quantityBackOrdered = quantity - totalquantityonhand;
    quantityCommitted = totalquantityonhand;
  } else {
    // Fully available scenario: stock covers full quantity
    quantityBackOrdered = 0;
    quantityCommitted = quantity;
  }

  return {
    quantity: quantity,
    quantityBackOrdered: quantityBackOrdered,
    quantityCommitted: quantityCommitted,
  };
}

/* =======================
   Subscription UI helpers
   ======================= */

// Options for the interval <Dropdown>
export const SUBSCRIPTION_INTERVAL_OPTIONS = [
  { value: "1", label: "Every 1 month" },
  { value: "2", label: "Every 2 months" },
  { value: "3", label: "Every 3 months" },
  { value: "6", label: "Every 6 months" },
];

// Small date helpers we reuse across subscription UI
export function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function addMonthsSafe(date, months) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  const targetMonth = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const endDay = daysInMonth(targetYear, normalizedMonth);
  const clamped = Math.min(day, endDay);
  const res = new Date(d);
  res.setFullYear(targetYear, normalizedMonth, clamped);
  return res;
}

export function formatLocalDateToronto(date) {
  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "America/Toronto",
  });
}

// Same short "Aug 19, 2026" shape as formatLocalDateToronto, but for a raw
// NetSuite `trandate` (no fixed timezone — it's already a date, not an
// instant) and tolerant of an invalid/missing value.
// On-account ("pay later") checkout is for customers with real net payment
// terms in NetSuite — Net 10/15/30/45/60/75 — which `findByEmail` returns as
// `termsdays` (`term.daysuntilnetdue`) next to `terms` (id) and `termsname`.
// No terms, "Credit Card", "Prepaid" and "Due On Receipt" all mean pay by card
// up front. Business rule from Oct 21 2026; it replaced the Sep 2026
// `searchstage === "Customer"` test. The backend applies the same test on
// `POST /restapi/salesOrder` (order.controller.js), so hiding the tab is UX,
// not the guard.
export function hasNetTerms(userInfo) {
  return Number(userInfo?.termsdays) > 0;
}

// "Net 30", as NetSuite names the term — for copy that used to hardcode it.
export function paymentTermsLabel(userInfo) {
  return userInfo?.termsname || null;
}

// A NetSuite item name can carry an internal note after a caret —
// "Medicom: SafeBasics … 300/Bx ^ 1186-C". Everything from the "^" on is for
// staff, never for the storefront (Oct 2026). Use this wherever an `itemid`
// is shown to a customer as a name; the raw value stays raw for lookups,
// cart payloads and image sorting.
export function publicItemName(name) {
  const text = String(name ?? "");
  const caret = text.indexOf("^");
  return (caret === -1 ? text : text.slice(0, caret)).trim();
}

export function formatOrderDate(trandate) {
  const d = new Date(trandate);
  if (isNaN(d)) return trandate || "—";
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function nextFromToday(intervalStr) {
  const n = parseInt(intervalStr || "1", 10);
  return addMonthsSafe(new Date(), Number.isFinite(n) ? n : 1);
}

/**
 * Normalize a backend recurring-order record into a single, predictable shape.
 * Works with minor field-name differences coming from SuiteQL/REST.
 */
/**
 * Normalize a backend recurring-order record into a single, predictable shape.
 * Works with minor field-name differences coming from SuiteQL/REST.
 */
// config.js
// config.js
export function normalizeSubscriptionRecord(r) {
  const roId = r.id ?? r.internalid ?? r.roId;

  // Try every common place NetSuite may put the item
  const raw =
    r.item ??
    r.custrecord_ro_item ??
    r.custrecord_item ??
    r.iteminternalid ??
    r.item_internalid ??
    r.itemId ??
    r.item_id ??
    r.internalid_item ??
    r.item_internal_id ??
    r.productId ??
    null;

  // Extract a numeric internalid from any shape
  const pickNumericId = (v) => {
    if (v == null) return undefined;

    if (Array.isArray(v)) {
      for (const el of v) {
        const got = pickNumericId(el);
        if (got) return got;
      }
      return undefined;
    }

    if (typeof v === "object") {
      const candidate =
        v.id ??
        v.internalid ??
        v.internalId ??
        v.value ??
        v.itemid ??
        v.itemId ??
        v.item_id ??
        v.item;
      return pickNumericId(candidate);
    }

    const s = String(v).trim();
    if (!s || s === "[object Object]") return undefined;

    // JSON payload like {"id":"20412"} or [{"id":20412}]
    if (
      (s.startsWith("{") && s.endsWith("}")) ||
      (s.startsWith("[") && s.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(s);
        return pickNumericId(parsed);
      } catch {
        /* ignore */
      }
    }

    // Accept things like "20412", "id=20412", "internalid:20412"
    const m = s.match(/(\d{3,})/);
    return m ? m[1] : undefined;
  };

  const productId = pickNumericId(raw); // numeric string if we found one

  return {
    roId,
    productId,
    itemid:
      r.itemid ||
      r.itemName ||
      r.name ||
      r.displayname ||
      `#${productId || roId}`,
    displayname:
      r.displayname ||
      r.itemName ||
      r.name ||
      r.itemid ||
      `#${productId || roId}`,
    file_url: r.file_url || r.image || r.thumbnail || r.fileUrl || "",
    interval: String(
      r.interval || r.custrecord_ro_interval || r.recurringInterval || "1"
    ),
  };
}

/** Detect if a recurring order is canceled (status "3") regardless of field shape */
export function isSubscriptionCanceled(rec) {
  const val =
    rec?.custrecord_ro_status?.id ??
    rec?.custrecord_ro_status ??
    rec?.statusId ??
    rec?.status;
  return String(val) === "3";
}

/** Small payload helpers so we don’t repeat magic values in components */
export function buildIntervalPatchPayload(interval) {
  return { custrecord_ro_interval: Number(interval) };
}

/* ──────────────────────────────────────────────────────────────────────────
   Scheduling utilities (shared by PDP + Subscriptions)
   ────────────────────────────────────────────────────────────────────────── */
export const DateUtils = {
  // Keep helpers as properties to avoid top-level duplicate identifiers
  daysInMonth: (y, m) => new Date(y, m + 1, 0).getDate(),

  addMonthsSafe(date, months) {
    const day = date.getDate();
    const targetMonth = date.getMonth() + months;
    const targetYear = date.getFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;
    const endDay = this.daysInMonth(targetYear, normalizedMonth);
    const d = new Date(date);
    d.setFullYear(targetYear, normalizedMonth, Math.min(day, endDay));
    return d;
  },

  toInput(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  },

  fmtToronto(date) {
    return date.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "America/Toronto",
    });
  },

  nextSubscriptionDateFromToday(intervalStr) {
    const n = parseInt(intervalStr || "1", 10);
    return this.addMonthsSafe(new Date(), isNaN(n) ? 1 : n);
  },
};

/*export const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // Mon index 0..6
export const jsIdxFromMonIdx = (monIdx) => (monIdx + 1) % 7; // Mon0..Sun6 -> JS Sun0..Sat6
export const monIdxFromJsIdx = (jsIdx) => (jsIdx + 6) % 7; // JS Sun0..Sat6 -> Mon0..Sun6*/

// Convert day numbers (1-7) to readable text
/*export const formatDeliveryDays = (dayNumbers) => {
  if (!dayNumbers || dayNumbers.length === 0) return "No preferences set";

  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    dayNumbers
      .map((dayNum) => dayLabels[dayNum - 1]) // Convert 1-based to 0-based
      .filter(Boolean) // Remove undefined values
      .join(", ") || "No preferences set"
  );
};*/

/*export const nextDateForWeekdayFrom = (baseDate, monIdx) => {
  const jsTarget = jsIdxFromMonIdx(monIdx);
  const start = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate()
  );
  const diff = (jsTarget - start.getDay() + 7) % 7;
  const result = new Date(start);
  result.setDate(start.getDate() + diff);
  return result;
};*/

// config/config.js

// …existing imports and exports…

/** Resolve any non-numeric id to a numeric internal id via your search endpoint. */
export async function resolveProductIdByNameOrId(maybeId) {
  const s = String(maybeId || "").trim();
  if (/^\d+$/.test(s)) return s;

  const { default: endpoint } = await import("../api/endpoints.js");
  const { default: api } = await import("../api/api.js");

  const query = decodeURIComponent(s);
  const candidates = [
    query,
    query.replace(/\s*-\s*.*/, ""),
    query
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  ];

  for (const q of candidates) {
    try {
      const res = await api.post(
        endpoint.POST_GET_ITEMS_BY_NAME({ limit: 1 }),
        { name: q }
      );
      const arr = Array.isArray(res.data)
        ? res.data
        : res.data?.items || res.data;
      const first = Array.isArray(arr) ? arr[0] : undefined;
      if (first?.id) return String(first.id);
    } catch {
      /* continue */
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Preferred Delivery Days — single source of truth
// ─────────────────────────────────────────────────────────────
export const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const jsIdxFromMonIdx = (monIdx) => (monIdx + 1) % 7; // Mon1..Sun7 -> JS Sun0..Sat6
export const monIdxFromJsIdx = (jsIdx) => (jsIdx + 6) % 7; // JS Sun0..Sat6 -> Mon1..Sun7

export function formatDeliveryDays(dayNumbers) {
  // Handle different input types: array, string, or single number
  let nums = [];

  if (Array.isArray(dayNumbers)) {
    nums = dayNumbers;
  } else if (typeof dayNumbers === "string") {
    // Convert string like "1, 2" or "1,2,3" to array of numbers
    nums = dayNumbers
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s !== "");
  } else if (typeof dayNumbers === "number") {
    nums = [dayNumbers];
  }

  const cleaned = Array.from(
    new Set(
      nums.map(Number).filter((n) => Number.isFinite(n) && n >= 1 && n <= 7)
    )
  ).sort((a, b) => a - b);

  if (cleaned.length === 0) return "No preferences set";
  return cleaned.map((n) => DOW_LABELS[n - 1]).join(", ");
}

export function parsePreferredDays(value) {
  if (!value) return [];
  const parts = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[,\s]+/)
      : [value];
  return Array.from(
    new Set(
      parts
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 7)
    )
  ).sort((a, b) => a - b);
}

export function serializePreferredDays(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  const cleaned = Array.from(
    new Set(
      arr
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 7)
    )
  ).sort((a, b) => a - b);
  return cleaned.join(", ");
}

export const nextDateForWeekdayFrom = (baseDate, monIdx) => {
  const jsTarget = jsIdxFromMonIdx(monIdx);
  const start = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate()
  );
  const diff = (jsTarget - start.getDay() + 7) % 7;
  const result = new Date(start);
  result.setDate(start.getDate() + diff);
  return result;
};

/** Normalize any backend/custom-field shape into a simple string. */
export function normalizePrefToString(any) {
  if (any == null) return "";
  if (typeof any === "string" || typeof any === "number") return String(any);

  if (Array.isArray(any)) {
    const parts = any
      .map((el) => {
        if (el == null) return null;
        if (typeof el === "string" || typeof el === "number") return String(el);
        if (typeof el === "object") {
          return (
            el.id ??
            el.value ??
            el.text ??
            el.name ??
            (typeof el.label === "string" ? el.label : null)
          );
        }
        return null;
      })
      .filter(Boolean);
    return parts.join(", ");
  }

  if (typeof any === "object") {
    const maybeList = any.values ?? any.selected ?? any.ids ?? any.list;
    if (Array.isArray(maybeList)) return normalizePrefToString(maybeList);
    return normalizePrefToString(
      any.value ?? any.id ?? any.text ?? any.data ?? any.name ?? ""
    );
  }

  return "";
}

/** Text for Preferred Delivery Days from backend (top-level only) + localStorage fallback */
export function preferredDaysTextFromSources({
  userInfo,
  storageKey = "preferredDeliveryDays",
} = {}) {
  const rawBackend = normalizePrefToString(userInfo?.prefer_delivery);
  const rawLocal =
    typeof window !== "undefined"
      ? window.localStorage.getItem(storageKey) || ""
      : "";

  const arrBackend = parsePreferredDays(rawBackend);
  const arrLocal = parsePreferredDays(rawLocal);

  if (arrBackend.length) return formatDeliveryDays(arrBackend);
  if (rawBackend) return String(rawBackend);
  if (arrLocal.length) return formatDeliveryDays(arrLocal);
  if (rawLocal) return String(rawLocal);
  return "No preferences set";
}

// Calculate current price based on quantity and pricing tiers
export function getCurrentPrice(product, quantity, userCategory = null) {
  const qty = parseInt(quantity, 10);
  const basePrice = Number(product?.price) || 0;
  let currentPrice = basePrice;

  // First priority: Check for promotional fixedprice
  if (product?.fixedprice) {
    const fixedPrice = Number(product.fixedprice) || 0;

    // If itemquantifier exists, check if quantity meets requirement
    if (product?.itemquantifier) {
      const requiredQty = parseInt(product.itemquantifier, 10);
      if (qty >= requiredQty) {
        currentPrice = fixedPrice;
      }
    } else {
      // No minimum quantity requirement, apply promotion
      currentPrice = fixedPrice;
    }
  }

  // Second priority: Check quantity-based pricing tiers (only if no promo applied)
  if (
    currentPrice === basePrice &&
    product?.pricing &&
    product.pricing.length > 1
  ) {
    // Find the correct price tier
    for (let i = product.pricing.length - 1; i >= 0; i--) {
      const tier = product.pricing[i];
      const tierQty = parseInt(tier.quantity, 10);
      const tierPrice = Number(tier.price) || 0;

      if (qty >= tierQty) {
        currentPrice = tierPrice;
        break;
      }
    }
  }

  // Calculate savings
  const savePrice = basePrice - currentPrice;

  return {
    basePrice: Number(basePrice),
    currentPrice: Number(currentPrice),
    savePrice: savePrice > 0 ? Number(savePrice) : 0,
  };
}

/**
 * Hint for a Buy-X-Get-Y item whose promotion price isn't reached yet, e.g.
 * "Add 3 to cart to get promotion price" for Buy 2 Get 1 (N = buy + get).
 *
 * Returned only when all of these are true:
 * - the stock description is a Buy-X-Get-Y deal,
 * - the item has a promotion price with a minimum quantity,
 * - the quantity is still below that minimum.
 *
 * Otherwise returns `undefined`. Once the minimum is reached, the "You save" pill
 * shows instead.
 */
export function getPromoHint(product, quantity) {
  const { buy, get } = extractBuyGet(product?.stockdescription || "");
  const minimum = parseInt(product?.itemquantifier, 10);
  if (!buy || !get || !product?.fixedprice || !minimum) return undefined;
  if (parseInt(quantity, 10) >= minimum) return undefined;
  return `Add ${buy + get} to cart to get promotion price`;
}

export {
  extractBuyGet,
  getMatrixInfo,
  formatPrice,
  formatCurrency,
  truncateText,
  parsePaymentStatus,
  getPaymentMethodFromMemo,
  calculateTotalPrice,
  calculateTotalCurrency,
  calculateOrderTotal,
  estimateTaxPerLine,
  // getTotalPriceAfterDiscount,
  fetchLocationByPostalCode,
  calculateActualQuantity,
  createQuantityHandlers,
  useQuantityHandlers,
  fetchRegionByCode,
  validatePhone,
  phoneTenDigits,
  validatePassword,
  validateCanadianPostalCode,
  validateUSZipCode,
  validatePostalCode,
  handleTaxShippingEstimate,
  buildIdempotencyKey,
  duplicateOrderMessage,
  calculateNextRunDate,
  generateRandomPassword,
  calculateQuantityAllocation,
};
